import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let sid = sessionStorage.getItem('waka-session-id');
  if (!sid) {
    sid = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2));
    sessionStorage.setItem('waka-session-id', sid);
  }
  return sid;
}

// Rolling ring buffer of the last 30s of console errors — attached to bug reports.
const _consoleErrors = [];
if (typeof window !== 'undefined' && !window.__wakaConsoleHook) {
  window.__wakaConsoleHook = true;
  const origErr = console.error;
  console.error = (...args) => {
    try {
      _consoleErrors.push({ t: Date.now(), msg: args.map(a => String(a)).join(' ').slice(0, 500) });
      const cutoff = Date.now() - 30_000;
      while (_consoleErrors.length && _consoleErrors[0].t < cutoff) _consoleErrors.shift();
    } catch {}
    origErr.apply(console, args);
  };
  window.addEventListener('error', (e) => {
    _consoleErrors.push({ t: Date.now(), msg: `${e.message} @ ${e.filename}:${e.lineno}` });
  });
}
export function getRecentConsoleErrors() { return _consoleErrors.slice(-10); }

const FeedbackContext = createContext(null);

export function FeedbackProvider({ children }) {
  const { token, user } = useAuth();
  const [activePrompt, setActivePrompt] = useState(null);
  const [shownThisSession, setShownThisSession] = useState(0);

  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const firePrompt = useCallback(async (trigger) => {
    if (!token || !user) return;
    if (shownThisSession >= 1) return;
    // Read latest activePrompt lazily via setter to avoid stale closure
    let alreadyActive = false;
    setActivePrompt(p => { alreadyActive = !!p; return p; });
    if (alreadyActive) return;
    try {
      const r = await axios.post(
        `${API}/feedback/prompts/check`,
        { trigger, session_id: getSessionId() },
        { headers },
      );
      if (r.data?.show) {
        setActivePrompt({
          trigger: r.data.trigger,
          prompt_en: r.data.prompt_en,
          prompt_es: r.data.prompt_es,
        });
        setShownThisSession(n => n + 1);
      }
    } catch {}
  }, [token, user, shownThisSession, headers]);

  const answerPrompt = useCallback(async ({ rating, comment }) => {
    if (!activePrompt) return;
    try {
      await axios.post(`${API}/feedback`, {
        type: 'prompt',
        trigger: activePrompt.trigger,
        rating,
        comment: comment || '',
        url: window.location.pathname,
        session_id: getSessionId(),
      }, { headers });
      toast.success('Thanks!');
    } catch {}
    setActivePrompt(null);
  }, [activePrompt, headers]);

  const dismissPrompt = useCallback(async () => {
    if (!activePrompt) return;
    try {
      await axios.post(`${API}/feedback/prompts/${activePrompt.trigger}/dismiss`, {}, { headers });
    } catch {}
    setActivePrompt(null);
  }, [activePrompt, headers]);

  const submit = useCallback(async (payload) => {
    if (!token) return false;
    try {
      await axios.post(`${API}/feedback`, {
        ...payload,
        url: payload.url || window.location.pathname,
        session_id: getSessionId(),
        meta: {
          ...(payload.meta || {}),
          user_agent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          recent_console_errors: getRecentConsoleErrors(),
        },
      }, { headers });
      return true;
    } catch {
      return false;
    }
  }, [token, headers]);

  // Global bus so any component can fire without importing the hook
  useEffect(() => {
    window.__wakaFeedback = { fire: firePrompt };
    return () => { try { delete window.__wakaFeedback; } catch {} };
  }, [firePrompt]);

  const value = { activePrompt, firePrompt, answerPrompt, dismissPrompt, submit };
  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
}

export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) return { activePrompt: null, firePrompt: () => {}, answerPrompt: async () => {}, dismissPrompt: async () => {}, submit: async () => false };
  return ctx;
}

// Small helper to fire a trigger from anywhere, even without hook access.
export function fireFeedbackTrigger(trigger) {
  try { window.__wakaFeedback?.fire?.(trigger); } catch {}
}
